/**
 * Resultado da pré-análise de triagem (E45.F2): comparação das assinaturas com // ux-neutrality:allow doc-comment
 * o heatmap das marcações, indicativo em % com a régua binária, síntese e // ux-neutrality:allow doc-comment
 * os dois caminhos (seguir para o exame pericial ou encerrar na triagem). // ux-neutrality:allow doc-comment
 * Apoio PRELIMINAR — nunca entra no laudo nem encosta no LR (ADR-0056). // ux-neutrality:allow doc-comment
 *
 * E54.F6 — prancha 3A: cada bloco vira objeto de blueprint com kicker, a // ux-neutrality:allow doc-comment
 * legenda de convergência/divergência entra no rodapé da comparação e o portão // ux-neutrality:allow doc-comment
 * de decisão vira a faixa de rodapé da prancha (status à esquerda, os dois // ux-neutrality:allow doc-comment
 * caminhos à direita). As pranchas de espécime já vêm conformadas da F5 — a // ux-neutrality:allow doc-comment
 * comparação reusa o `CompareView` da mesa e não retoca a superfície de exame. // ux-neutrality:allow doc-comment
 */
import { Check } from 'lucide-react';
import { useId, useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import { CompareView } from '@/components/Workspace/CompareView';
import { useSignatureBlob } from '@/components/Workspace/useSignatureBlob';
import { Blueprint } from '@/components/ui/Blueprint';
import { CardKicker } from '@/components/ui/Card';
import { MonoData } from '@/components/ui/MonoData';
import type { SignatureRecordData } from '@/lib/case_signatures';
import type { PreAnaliseRecordData } from '@/lib/pre_analise';
import { IndicativeBar } from './IndicativeBar';
import { PadroesUsados } from './PadroesUsados';
import { PreAnaliseMarkerOverlay } from './PreAnaliseMarkerOverlay';
import { PreAnaliseShareSummary } from './PreAnaliseShareSummary';
import { TriagemFindings } from './TriagemFindings';

export interface PreAnaliseResultProps {
  caseId: string;
  preAnalise: PreAnaliseRecordData;
  contestedSrc: string | null;
  /**
   * TODOS os padrões do caso (E67.F1.T03). O motor sempre comparou a
   * questionada com o conjunto inteiro; a tela é que ilustrava com o primeiro.
   * A prancha mostra um por vez — são duas imagens lado a lado —, mas quem
   * escolhe qual é o perito, e o rodapé diz quantos entraram na conta.
   */
  padroes: SignatureRecordData[];
}

/**
 * Legenda da figura: o que cada cor de marcação quer dizer. Reusa a legenda de
 * marcação da mesa (`markers-legend`), que já lê a rampa de sinal do sistema —
 * legenda e traço saem do mesmo lugar e não podem divergir.
 */
function MarcacaoLegend(): JSX.Element {
  const itens: Array<[string, string]> = [
    ['markers-legend-item--convergent', ptBR.preAnalise.marcacaoConvergencia],
    ['markers-legend-item--divergent', ptBR.preAnalise.marcacaoDivergencia],
  ];
  return (
    <div
      className="markers-legend mt-sc-3 justify-end border-t border-border"
      data-testid="pre-analise-legenda"
    >
      {itens.map(([variante, rotulo]) => (
        <span key={rotulo} className={`markers-legend-item ${variante}`}>
          <MonoData variant="label">{rotulo}</MonoData>
        </span>
      ))}
    </div>
  );
}

function StatusLine({ status }: { status: PreAnaliseRecordData['status'] }): JSX.Element | null {
  if (status === 'seguiu_mesa') {
    return (
      <p
        className="flex items-center gap-sc-2 text-body-sm text-success-700"
        data-testid="pre-analise-status-seguiu"
        role="status"
      >
        <Check className="h-[18px] w-[18px] shrink-0 stroke-[1.5]" aria-hidden />
        {ptBR.preAnalise.statusSeguiuMesaMessage}
      </p>
    );
  }
  if (status === 'encerrada') {
    return (
      <p
        className="text-body-sm text-fg-muted"
        data-testid="pre-analise-status-encerrada"
        role="status"
      >
        {ptBR.preAnalise.statusEncerradaMessage}
      </p>
    );
  }
  // E72.F2: o desfecho com documento tem linha própria — dizer só "encerrado na
  // triagem" esconderia da pessoa que existe um relatório emitido no caso.
  if (status === 'encerrada_com_relatorio') {
    return (
      <p
        className="text-body-sm text-fg-muted"
        data-testid="pre-analise-status-encerrada-com-relatorio"
        role="status"
      >
        {ptBR.preAnalise.statusEncerradaComRelatorioMessage}
      </p>
    );
  }
  return null;
}

export function PreAnaliseResult({
  caseId,
  preAnalise,
  contestedSrc,
  padroes,
}: PreAnaliseResultProps): JSX.Element {
  const [padraoExibidoId, setPadraoExibidoId] = useState<string | null>(null);
  const padraoExibido = padroes.find((p) => p.id === padraoExibidoId) ?? padroes[0] ?? null;
  const patternSrc = useSignatureBlob(caseId, padraoExibido?.id);
  // E67.F1.C02: o resultado aparece uma vez POR questionada (N na mesma
  // página). Um `id` fixo viraria N elementos com o mesmo id, e o
  // `aria-labelledby` de todos eles apontaria para o primeiro — o leitor de tela
  // anunciaria sempre a comparação da primeira questionada.
  const instancia = useId();
  const comparacaoTitleId = `pre-analise-comparacao-title-${instancia}`;
  const sinteseTitleId = `pre-analise-sintese-title-${instancia}`;

  return (
    <section data-testid="pre-analise-resultado" aria-labelledby="pre-analise-title">
      <Blueprint
        as="section"
        className="p-sc-6"
        aria-labelledby={comparacaoTitleId}
        data-testid="pre-analise-comparacao"
      >
        <CardKicker id={comparacaoTitleId}>{ptBR.preAnalise.comparacaoSectionTitle}</CardKicker>
        {/* E54.F11.C02: a comparação K|Q tem piso de 36rem por natureza — são
            duas pranchas lado a lado, e estreitá-las destruiria a leitura que
            elas existem para permitir. Num aparelho de 390px ela passa a rolar
            DENTRO da própria caixa, em vez de arrastar a tela inteira. */}
        <div className="mt-sc-4 min-w-0 overflow-x-auto">
          <CompareView
            patternSrc={patternSrc}
            contestedSrc={contestedSrc}
            showMarkers={false}
            localizations={null}
            criteriaStatus={null}
            rulerActive={false}
            zoomCommand={null}
            syncPanels
            annotationsLayer={<PreAnaliseMarkerOverlay marcacoes={preAnalise.marcacoes} />}
          />
        </div>
        <PadroesUsados
          padroes={padroes}
          exibidoId={padraoExibido?.id ?? null}
          onExibir={setPadraoExibidoId}
        />
        <MarcacaoLegend />
      </Blueprint>

      <IndicativeBar
        indicativoPercentual={preAnalise.indicativo_percentual}
        tendencia={preAnalise.tendencia}
      />

      <Blueprint as="section" className="mt-sc-6 p-sc-6" aria-labelledby={sinteseTitleId}>
        <CardKicker id={sinteseTitleId}>{ptBR.preAnalise.sinteseSectionTitle}</CardKicker>
        <p
          className="mt-sc-3 text-body-md leading-relaxed text-fg"
          data-testid="pre-analise-sintese"
        >
          {preAnalise.sintese}
        </p>
        <TriagemFindings marcacoes={preAnalise.marcacoes} />
      </Blueprint>

      {preAnalise.status === 'encerrada' && <PreAnaliseShareSummary preAnalise={preAnalise} />}

      {preAnalise.status !== 'em_triagem' && (
        <div className="mt-sc-8 border-t border-border pt-sc-4">
          <StatusLine status={preAnalise.status} />
        </div>
      )}
    </section>
  );
}
